import React, { useState, useEffect } from "react";

// ── Design tokens ──────────────────────────────────────────────────────────────
const NAVY  = "#1e3a8a";
const BLUE  = "#2563eb";
const SKY   = "#0ea5e9";
const LIGHT = "#f0f7ff";
const LIGHT2 = "#e0f2fe";

const FEATURES = [
  {
    id: "video", icon: "🎥", tag: "AI HIRING", color: SKY,
    title: "AI Video Interview Screening",
    desc: "Send candidates a video interview link — Gemini AI watches every answer, scores it against your rubric, and delivers a ranked shortlist. No recruiter time needed.",
    bullets: ["Gemini scores each video answer on communication, depth & fit", "Auto-generates job-specific questions from your JD", "Overall candidate score + reasoning — ready in minutes after submission"],
  },
  {
    id: "recruitment", icon: "⚡", tag: "RECRUITMENT", color: BLUE,
    title: "AI CV Screening & Hiring",
    desc: "Post jobs, get a branded public careers page, and let AI score every CV the moment it lands. Rank candidates, generate offer letters, and post to LinkedIn — all in one flow.",
    bullets: ["AI scores & ranks CVs against your JD with skill match breakdown", "Branded careers page — apply form, PDPA consent, auto-acknowledgement", "AI offer letters, PDF-ready in one click"],
  },
  {
    id: "performance", icon: "🎯", tag: "PEOPLE", color: SKY,
    title: "Performance & Goals",
    desc: "Run structured review cycles with self-assessments, manager ratings, 360° peer feedback, and HR calibration — all in one place.",
    bullets: ["Self, manager & 360° peer feedback", "Goal tracking with progress % per employee", "Calibration view, PDF export & analytics"],
  },
  {
    id: "leave", icon: "📅", tag: "HR CORE", color: NAVY,
    title: "Leave & Attendance",
    desc: "Employees apply for leave and check in daily from any device. Managers approve in one click. Full balance, holiday, and WFH tracking.",
    bullets: ["Real-time leave balances + carry-forward", "Check-in/out with WFH & project tagging", "Regularization requests with manager approval"],
  },
  {
    id: "expenses", icon: "💼", tag: "EMPLOYEE", color: BLUE,
    title: "Expenses & Assets",
    desc: "Employees submit expense claims with receipts. Track company assets from purchase to assignment. Manage offboarding clearance checklists.",
    bullets: ["Expense claims with receipt upload & approval", "Asset register — laptops, phones, equipment", "Offboarding checklist + final settlement"],
  },
  {
    id: "finance", icon: "💸", tag: "FINANCE", color: NAVY,
    title: "Payroll & Finance",
    desc: "Run payroll for Thailand and India with full statutory compliance. Issue salary slips, create invoices and quotations — all branded and PDF-ready.",
    bullets: ["Thailand SSF/PVD + India PF/ESI/PT — auto-calculated", "Invoices & quotations with tax, TDS, WHT & discount", "PND.1, SSO.10, Form 16, bank payment file exports"],
  },
  {
    id: "ai", icon: "🤖", tag: "AI", color: SKY,
    title: "AI Assistant & HR Docs",
    desc: "An always-on HR chatbot answers employee queries 24/7 — and actually knows your data. Upload company policies as scanned PDFs; Gemini reads them and answers questions from the content.",
    bullets: ["Role-aware chatbot: knows leave, payslips, assets, policies & timesheets", "11 HR letter types — AI-drafted, fully editable, PDF with branding", "Upload scanned policy docs — Gemini OCR makes them searchable instantly"],
  },
];

const STATS = [
  { value: "10×",  label: "Faster CV shortlisting",     icon: "⚡" },
  { value: "5 min",label: "Setup to live",               icon: "🚀" },
  { value: "7",    label: "AI features, one platform",   icon: "🤖" },
  { value: "24/7", label: "AI assistant — always on",    icon: "🌐" },
];

const TRUST = [
  { icon: "🔒", label: "GDPR Ready"           },
  { icon: "⚡", label: "99.9% Uptime"         },
  { icon: "🛡️", label: "256-bit SSL"          },
  { icon: "✦",  label: "Powered by Gemini"    },
  { icon: "🌏", label: "TH · IN · Multi-region" },
  { icon: "📦", label: "No setup required"    },
];

// ── Feature Mockups (light theme) ─────────────────────────────────────────────
function FeatureMockup({ id, color }: { id: string; color: string }) {
  const card: React.CSSProperties = {
    background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "12px 14px",
    display: "flex", alignItems: "center", gap: 12, marginBottom: 8,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
  };

  if (id === "video") return (
    <div>
      {/* Question card */}
      <div style={{ background: LIGHT, border: `1px solid ${LIGHT2}`, borderRadius: 10, padding: "10px 14px", marginBottom: 10 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: SKY, letterSpacing: 0.8, marginBottom: 4 }}>QUESTION 2 OF 4</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a", lineHeight: 1.5 }}>
          "Describe how you handled a situation where you had to meet a tight deadline under pressure."
        </div>
      </div>
      {/* Fake video player */}
      <div style={{ background: "#0f172a", borderRadius: 10, height: 90, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 10, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(135deg, #1e3a8a44, #0ea5e944)", borderRadius: 10 }} />
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, zIndex: 1 }}>▶</div>
        <div style={{ position: "absolute", bottom: 8, right: 10, fontSize: 10, color: "rgba(255,255,255,0.6)", zIndex: 1 }}>0:47</div>
        <div style={{ position: "absolute", bottom: 8, left: 10, fontSize: 10, color: "rgba(255,255,255,0.5)", zIndex: 1 }}>● REC</div>
      </div>
      {/* AI score per answer */}
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 14px", marginBottom: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}>Gemini AI Score</span>
          <span style={{ fontSize: 18, fontWeight: 900, color: "#16a34a" }}>9.1<span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>/10</span></span>
        </div>
        <div style={{ fontSize: 11, color: "#64748b", lineHeight: 1.6 }}>
          Strong structure, specific example with measurable outcome. Confident delivery, maintained eye contact.
        </div>
      </div>
      {/* Overall badge */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#dcfce7", border: "1px solid #86efac", borderRadius: 8, padding: "8px 14px" }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#166534" }}>Overall Score</span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 900, color: "#166534" }}>8.7/10</span>
          <span style={{ background: "#166534", color: "white", fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 20 }}>Recommend</span>
        </div>
      </div>
    </div>
  );

  if (id === "recruitment") return (
    <div>
      <div style={{ background: LIGHT, border: `1px solid ${LIGHT2}`, borderRadius: 8, padding: "8px 12px", marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: NAVY }}>🌐 careers.hrxai.com/acme-corp</div>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#16a34a", background: "#dcfce7", padding: "2px 8px", borderRadius: 4 }}>● Live</span>
      </div>
      {[{ name: "Sarah Chen", role: "Senior React Dev", score: 94 },
        { name: "Marcus Wright", role: "Full Stack Engineer", score: 87 },
        { name: "Priya Sharma", role: "Frontend Developer", score: 72 }].map(c => (
        <div key={c.name} style={card}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: `linear-gradient(135deg,${color},${SKY})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "white", flexShrink: 0 }}>{c.name[0]}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{c.name}</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>{c.role}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: c.score > 90 ? "#16a34a" : c.score > 80 ? BLUE : "#94a3b8" }}>{c.score}%</div>
            <div style={{ fontSize: 10, color: "#94a3b8" }}>AI Match</div>
          </div>
        </div>
      ))}
      <div style={{ textAlign: "right", marginTop: 4 }}>
        <span style={{ background: `linear-gradient(135deg,${color},${SKY})`, color: "white", fontSize: 11, fontWeight: 600, padding: "6px 14px", borderRadius: 6, display: "inline-block" }}>Generate Offer Letter →</span>
      </div>
    </div>
  );

  if (id === "leave") return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
        {[{ label: "Annual", left: 8, total: 20, c: BLUE }, { label: "Sick", left: 3, total: 10, c: SKY }, { label: "WFH days", left: 4, total: 8, c: NAVY }].map(b => (
          <div key={b.label} style={{ background: LIGHT, border: `1px solid ${LIGHT2}`, borderRadius: 10, padding: "10px 8px", textAlign: "center" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: b.c }}>{b.left}</div>
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>{b.label}</div>
            <div style={{ marginTop: 5, height: 3, borderRadius: 2, background: "#e2e8f0" }}>
              <div style={{ width: `${((b.total - b.left) / b.total) * 100}%`, height: "100%", borderRadius: 2, background: b.c }} />
            </div>
          </div>
        ))}
      </div>
      {[{ name: "Alex Johnson", type: "Annual Leave · Jun 10–14", status: "pending", icon: "📅" },
        { name: "Ravi Kumar", type: "Checked in · WFH today", status: "present", icon: "🏠" },
        { name: "Maria Garcia", type: "Sick Leave · Jun 7", status: "approved", icon: "🏥" }].map(l => (
        <div key={l.name} style={{ ...card, marginBottom: 8 }}>
          <div style={{ fontSize: 16, flexShrink: 0 }}>{l.icon}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{l.name}</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>{l.type}</div>
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, padding: "3px 9px", borderRadius: 5, background: l.status === "approved" || l.status === "present" ? "#dcfce7" : "#fef3c7", color: l.status === "approved" || l.status === "present" ? "#16a34a" : "#d97706" }}>{l.status}</span>
        </div>
      ))}
    </div>
  );

  if (id === "finance") return (
    <div>
      <div style={{ background: LIGHT, border: `1px solid ${LIGHT2}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, paddingBottom: 8, borderBottom: "1px solid #e2e8f0" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>June Payroll</div>
            <div style={{ fontSize: 10, color: "#94a3b8" }}>28 employees</div>
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, color: NAVY }}>$84,200</div>
        </div>
        {[{ label: "Basic Salary", amount: "$72,000", type: "earn" }, { label: "Allowances", amount: "+$8,400", type: "earn" }, { label: "Tax", amount: "-$5,400", type: "ded" }, { label: "Net Pay", amount: "$75,000", type: "net" }].map(r => (
          <div key={r.label} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 11 }}>
            <span style={{ color: "#64748b" }}>{r.label}</span>
            <span style={{ fontWeight: r.type === "net" ? 700 : 500, color: r.type === "ded" ? "#dc2626" : r.type === "net" ? NAVY : "#1e293b" }}>{r.amount}</span>
          </div>
        ))}
      </div>
      {[{ client: "TechCorp Ltd", amount: "$12,400", status: "paid" },
        { client: "Nexus Solutions", amount: "$8,750", status: "overdue" }].map(inv => (
        <div key={inv.client} style={{ ...card, border: `1px solid ${inv.status === "overdue" ? "#fecaca" : "#e2e8f0"}`, background: inv.status === "overdue" ? "#fff5f5" : "white", marginBottom: 8 }}>
          <div style={{ width: 28, height: 28, borderRadius: 6, background: `linear-gradient(135deg,${NAVY},${BLUE})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "white", flexShrink: 0 }}>🧾</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{inv.client}</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>Invoice</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 12, fontWeight: 700 }}>{inv.amount}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: inv.status === "paid" ? "#16a34a" : "#dc2626" }}>{inv.status}</div>
          </div>
        </div>
      ))}
    </div>
  );


  if (id === "ai") return (
    <div>
      {[{ from: "user", text: "How many leave days do I have left?" },
        { from: "bot", text: "You have 8 annual leave days remaining. Next public holiday is June 2nd." },
        { from: "user", text: "Generate an experience letter for Arjun Mehta." },
        { from: "bot", text: "Done ✓ Experience certificate drafted for Arjun Mehta — 3 years 2 months. Ready to issue as PDF." }].map((m, i) => (
        <div key={i} style={{ display: "flex", justifyContent: m.from === "user" ? "flex-end" : "flex-start", marginBottom: 8 }}>
          <div style={{
            maxWidth: "82%", padding: "8px 12px", lineHeight: 1.6, fontSize: 11,
            borderRadius: m.from === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
            background: m.from === "user" ? `linear-gradient(135deg,${NAVY},${BLUE})` : LIGHT,
            border: m.from === "bot" ? `1px solid ${LIGHT2}` : "none",
            color: m.from === "user" ? "white" : "#1e293b",
          }}>{m.text}</div>
        </div>
      ))}
      <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5, marginTop: 8 }}>
        {["Offer Letter", "Experience Cert", "Increment", "Appointment"].map(t => (
          <span key={t} style={{ background: LIGHT, border: `1px solid ${LIGHT2}`, color: NAVY, fontSize: 10, fontWeight: 600, padding: "3px 9px", borderRadius: 20 }}>{t}</span>
        ))}
      </div>
    </div>
  );


  if (id === "performance") return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        {[
          { label: "Self Assessment", status: "Done", c: "#16a34a", bg: "#dcfce7" },
          { label: "Manager Review", status: "Pending", c: "#d97706", bg: "#fef3c7" },
          { label: "Peer Feedback", status: "2/3 done", c: BLUE, bg: LIGHT },
          { label: "HR Calibration", status: "Pending", c: "#94a3b8", bg: "#f1f5f9" },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 10, color: "#64748b", marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: s.c }}>{s.status}</div>
          </div>
        ))}
      </div>
      {[
        { name: "Aisha Patel", role: "Product Designer", rating: 4.5, trend: "▲" },
        { name: "James Kim", role: "Backend Engineer", rating: 3.8, trend: "→" },
      ].map(r => (
        <div key={r.name} style={{ ...card, marginBottom: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", background: `linear-gradient(135deg,${color},${SKY})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: "white", flexShrink: 0 }}>{r.name[0]}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{r.name}</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>{r.role}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: r.rating >= 4 ? "#16a34a" : BLUE }}>{r.rating}</div>
            <div style={{ fontSize: 10, color: "#94a3b8" }}>{r.trend} rating</div>
          </div>
        </div>
      ))}
    </div>
  );

  if (id === "expenses") return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 10 }}>
        {[{ label: "Pending Claims", val: "$340", c: "#d97706", bg: "#fef3c7" }, { label: "Assets Assigned", val: "12", c: BLUE, bg: LIGHT }, { label: "Approved this month", val: "$1,240", c: "#16a34a", bg: "#dcfce7" }, { label: "Offboarding", val: "1 active", c: "#94a3b8", bg: "#f1f5f9" }].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: s.c }}>{s.val}</div>
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>
      {[
        { name: "Omar Hassan", action: "Travel expense · $142", status: "pending", icon: "🧾" },
        { name: "Lisa Tan", action: "MacBook Pro · Assigned", status: "asset", icon: "💻" },
        { name: "Arjun Mehta", action: "Meals · $38", status: "approved", icon: "🍽️" },
      ].map(a => (
        <div key={a.name} style={{ ...card, marginBottom: 8 }}>
          <div style={{ fontSize: 15, flexShrink: 0 }}>{a.icon}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>{a.name}</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>{a.action}</div>
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: a.status === "approved" ? "#dcfce7" : a.status === "asset" ? LIGHT : "#fef3c7", color: a.status === "approved" ? "#16a34a" : a.status === "asset" ? BLUE : "#d97706" }}>{a.status}</span>
        </div>
      ))}
    </div>
  );



  return null;
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [email, setEmail] = useState("");
  const [activeFeature, setActiveFeature] = useState("recruitment");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    window.location.href = email ? `/signup?email=${encodeURIComponent(email)}` : "/signup";
  };

  const activeF = FEATURES.find(f => f.id === activeFeature) ?? FEATURES[0];

  return (
    <div style={{ fontFamily: "var(--font-sans)", color: "#1e293b", background: "white", overflowX: "hidden" }}>
      <style>{`
        * { box-sizing: border-box; }
        @keyframes marquee { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        @keyframes fade-in  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes float    { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-8px)} }
        .cta-primary { background: linear-gradient(135deg,${NAVY},${BLUE}); color: white; border: none; border-radius: 10px; padding: 14px 28px; font-size: 15px; font-weight: 700; cursor: pointer; transition: all 0.2s; box-shadow: 0 4px 14px rgba(30,58,138,0.3); text-decoration: none; display: inline-block; }
        .cta-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(30,58,138,0.4); }
        .cta-secondary { background: white; color: ${NAVY}; border: 1.5px solid #dbeafe; border-radius: 10px; padding: 13px 26px; font-size: 15px; font-weight: 600; cursor: pointer; transition: all 0.2s; text-decoration: none; display: inline-block; }
        .cta-secondary:hover { background: ${LIGHT}; border-color: ${BLUE}; }
        .nav-link { color: #475569; text-decoration: none; font-size: 14px; font-weight: 500; padding: 8px 14px; border-radius: 8px; transition: all 0.15s; }
        .nav-link:hover { color: ${NAVY}; background: ${LIGHT}; }
        .feature-tab:hover { background: ${LIGHT} !important; }
        .trust-pill:hover { border-color: #bfdbfe !important; background: ${LIGHT} !important; }

        /* ── Responsive ── */
        @media(max-width:768px){
          .hero-ctas   { flex-direction: column !important; width: 100% !important; }
          .hero-ctas a,.hero-ctas button { width: 100% !important; text-align: center !important; }
          .hero-title  { font-size: 36px !important; letter-spacing: -1px !important; }
          .hero-sub    { font-size: 15px !important; }
          .hero-badge  { font-size: 11px !important; }
          .hero-preview{ display: none !important; }
          .nav-desktop { display: none !important; }
          .nav-mobile-btn { display: flex !important; }
          .stats-grid  { grid-template-columns: 1fr 1fr !important; }
          .features-grid { grid-template-columns: 1fr !important; }
          .steps-grid  { grid-template-columns: 1fr !important; gap: 32px !important; }
          .steps-line  { display: none !important; }
          .footer-grid { flex-direction: column !important; align-items: flex-start !important; gap: 32px !important; }
          .compare-grid { grid-template-columns: 1fr !important; }
          .hero-form   { flex-direction: column !important; }
          .hero-form input  { border-radius: 10px !important; border-right: 1.5px solid #dbeafe !important; }
          .hero-form button { border-radius: 10px !important; width: 100% !important; }
          .trust-row   { gap: 8px !important; }
          .section-pad { padding: 64px 20px !important; }
          .hero-pad    { padding: 100px 20px 64px !important; }
          .nav-pad     { padding: 0 20px !important; }
          .feature-mockup-col { display: none !important; }
        }
        @media(max-width:480px){
          .hero-title { font-size: 30px !important; }
          .stat-val   { font-size: 36px !important; }
        }
      `}</style>

      {/* ── Navbar ────────────────────────────────────────────────────────────── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 300,
        background: scrolled ? "rgba(255,255,255,0.95)" : "white",
        backdropFilter: scrolled ? "blur(16px)" : "none",
        borderBottom: `1px solid ${scrolled ? "#e2e8f0" : "#f1f5f9"}`,
        transition: "all 0.25s",
        boxShadow: scrolled ? "0 1px 12px rgba(0,0,0,0.06)" : "none",
      }}>
        <div className="nav-pad" style={{ maxWidth: 1140, margin: "0 auto", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 32px" }}>
          {/* Logo */}
          <a href="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <img src="/favicon.png" alt="HRxAI" style={{ height: 36, width: 36, objectFit: "contain", borderRadius: 8 }} />
            <span style={{ fontWeight: 800, fontSize: 18, color: NAVY, letterSpacing: -0.4 }}>
              HRx<span style={{ color: SKY }}>AI</span>
            </span>
          </a>

          {/* Desktop nav */}
          <div className="nav-desktop" style={{ display: "flex", alignItems: "center", gap: 2 }}>
            <a href="#features"    className="nav-link">Features</a>
            <a href="#how-it-works" className="nav-link">How it works</a>
            <a href="/pricing"      className="nav-link">Pricing</a>
            <a href="/docs"         className="nav-link">Docs</a>
          </div>

          <div className="nav-desktop" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <a href="/login" className="cta-secondary" style={{ padding: "8px 20px", fontSize: 14 }}>Sign in</a>
            <a href="/signup" className="cta-primary" style={{ padding: "9px 20px", fontSize: 14, boxShadow: "0 4px 12px rgba(30,58,138,0.25)" }}>Get started →</a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="nav-mobile-btn"
            onClick={() => setMenuOpen(o => !o)}
            style={{ display: "none", background: "none", border: "none", cursor: "pointer", padding: 8, color: NAVY, flexDirection: "column", gap: 5 }}
          >
            <span style={{ display: "block", width: 22, height: 2, background: NAVY, borderRadius: 2, transition: "all 0.2s", transform: menuOpen ? "rotate(45deg) translateY(7px)" : "none" }} />
            <span style={{ display: "block", width: 22, height: 2, background: NAVY, borderRadius: 2, transition: "all 0.2s", opacity: menuOpen ? 0 : 1 }} />
            <span style={{ display: "block", width: 22, height: 2, background: NAVY, borderRadius: 2, transition: "all 0.2s", transform: menuOpen ? "rotate(-45deg) translateY(-7px)" : "none" }} />
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div style={{ borderTop: "1px solid #f1f5f9", padding: "16px 20px 20px", display: "flex", flexDirection: "column", gap: 4, background: "white" }}>
            <a href="#features"     className="nav-link" onClick={() => setMenuOpen(false)} style={{ display: "block", padding: "10px 14px" }}>Features</a>
            <a href="#how-it-works" className="nav-link" onClick={() => setMenuOpen(false)} style={{ display: "block", padding: "10px 14px" }}>How it works</a>
            <a href="/pricing"      className="nav-link" onClick={() => setMenuOpen(false)} style={{ display: "block", padding: "10px 14px" }}>Pricing</a>
            <a href="/docs"         className="nav-link" onClick={() => setMenuOpen(false)} style={{ display: "block", padding: "10px 14px" }}>Docs</a>
            <div style={{ height: 1, background: "#f1f5f9", margin: "8px 0" }} />
            <a href="/login"  className="cta-secondary" style={{ textAlign: "center", marginBottom: 8 }}>Sign in</a>
            <a href="/signup" className="cta-primary"   style={{ textAlign: "center" }}>Get started →</a>
          </div>
        )}
      </nav>

      {/* ── Hero ──────────────────────────────────────────────────────────────── */}
      <section className="hero-pad" style={{
        paddingTop: 120, paddingBottom: 80, paddingLeft: 24, paddingRight: 24,
        background: "linear-gradient(180deg, #f0f7ff 0%, #ffffff 100%)",
        textAlign: "center",
        position: "relative", overflow: "hidden",
      }}>
        {/* Decorative background circles */}
        <div style={{ position: "absolute", top: -80, left: "50%", transform: "translateX(-50%)", width: 700, height: 700, borderRadius: "50%", background: "radial-gradient(circle, #dbeafe 0%, transparent 70%)", pointerEvents: "none", opacity: 0.6 }} />

        <div style={{ maxWidth: 820, margin: "0 auto", position: "relative" }}>
          {/* Badge */}
          <div className="hero-badge" style={{ display: "inline-flex", alignItems: "center", gap: 8, background: LIGHT, border: `1px solid ${LIGHT2}`, borderRadius: 100, padding: "6px 16px", marginBottom: 28, fontSize: 12, color: NAVY, fontWeight: 600 }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: SKY, display: "inline-block" }} />
            Powered by Gemini · AI Video Screening + Full HR Suite
          </div>

          <h1 className="hero-title" style={{ fontSize: 60, fontWeight: 900, lineHeight: 1.08, letterSpacing: -2, margin: "0 0 22px", color: "#0f172a" }}>
            Hire smarter.{" "}
            <span style={{ background: `linear-gradient(135deg,${NAVY},${SKY})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              Run HR on AI.
            </span>
          </h1>

          <p className="hero-sub" style={{ fontSize: 18, color: "#475569", lineHeight: 1.8, maxWidth: 600, margin: "0 auto 40px" }}>
            AI video interview screening, CV scoring, payroll compliance for Thailand & India, and an always-on HR chatbot — one platform, and you pay only for the modules you use. Tools that cost $600+/mo elsewhere, from $79.
          </p>

          {/* CTA buttons */}
          <div className="hero-ctas" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginBottom: 20 }}>
            <a href="/signup" className="cta-primary">Get started →</a>
            <a href="#features" className="cta-secondary">See Features</a>
          </div>
          <p style={{ color: "#94a3b8", fontSize: 12, margin: "0 0 56px" }}>
            Core from $79/mo · Instant activation · Cancel anytime
          </p>

          {/* Hero dashboard preview */}
          <div className="hero-preview" style={{ position: "relative", maxWidth: 860, margin: "0 auto", animation: "float 6s ease-in-out infinite" }}>
            <div style={{
              background: "white", borderRadius: 18, border: "1px solid #e2e8f0",
              boxShadow: "0 24px 64px rgba(30,58,138,0.12), 0 4px 16px rgba(0,0,0,0.06)",
              padding: "20px 24px", overflow: "hidden",
            }}>
              {/* Browser dots */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 18, paddingBottom: 14, borderBottom: "1px solid #f1f5f9" }}>
                {["#ef4444","#f59e0b","#22c55e"].map(c => <div key={c} style={{ width: 10, height: 10, borderRadius: "50%", background: c }} />)}
                <div style={{ flex: 1, background: "#f8fafc", borderRadius: 6, height: 20, marginLeft: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 10, color: "#94a3b8" }}>www.hrxai.app/dashboard</span>
                </div>
              </div>
              {/* Stat cards */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 18 }}>
                {[{ label: "Pending Leaves", value: "12", color: "#f59e0b", bg: "#fffbeb" }, { label: "Open Jobs", value: "5", color: BLUE, bg: LIGHT }, { label: "Draft Payslips", value: "28", color: "#16a34a", bg: "#f0fdf4" }, { label: "Overdue Invoices", value: "3", color: "#dc2626", bg: "#fef2f2" }].map(s => (
                  <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: "12px 14px", border: "1px solid #f1f5f9" }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              {/* Bar chart */}
              <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 48 }}>
                {[30,55,40,70,60,85,45,90,65,50,75,80].map((h, i) => (
                  <div key={i} style={{ flex: 1, height: `${h}%`, borderRadius: "3px 3px 0 0", background: i === 7 ? `linear-gradient(180deg,${SKY},${NAVY})` : "#e0f2fe" }} />
                ))}
              </div>
            </div>

            {/* Floating chips */}
            <div style={{ position: "absolute", top: -12, left: -12, background: "white", border: "1px solid #dbeafe", borderRadius: 12, padding: "10px 14px", boxShadow: "0 4px 16px rgba(30,58,138,0.1)" }}>
              <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 2 }}>AI Match Score</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: NAVY }}>94%</div>
              <div style={{ fontSize: 10, color: "#64748b" }}>Senior React Dev</div>
            </div>
            <div style={{ position: "absolute", bottom: -12, right: -12, background: "white", border: "1px solid #dbeafe", borderRadius: 12, padding: "10px 14px", boxShadow: "0 4px 16px rgba(30,58,138,0.1)", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", background: `linear-gradient(135deg,${NAVY},${SKY})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>🤖</div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}>HR Assistant</div>
                <div style={{ fontSize: 10, color: "#16a34a", fontWeight: 600 }}>● Online</div>
              </div>
            </div>
            <div style={{ position: "absolute", top: -12, right: 80, background: "white", border: "1px solid #dbeafe", borderRadius: 10, padding: "7px 12px", boxShadow: "0 4px 16px rgba(30,58,138,0.1)", display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ fontSize: 13 }}>✦</span>
              <div style={{ fontSize: 10, fontWeight: 700, color: NAVY }}>Powered by Gemini</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Trust badges ──────────────────────────────────────────────────────── */}
      <section style={{ background: "white", borderTop: "1px solid #f1f5f9", borderBottom: "1px solid #f1f5f9", padding: "28px 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: "#94a3b8", letterSpacing: 1, textTransform: "uppercase", marginBottom: 16 }}>Built for modern teams</p>
          <div className="trust-row" style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
            {TRUST.map(b => (
              <div key={b.label} className="trust-pill" style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 16px", borderRadius: 100, border: "1px solid #e2e8f0", background: "#fafafa", color: "#475569", fontSize: 13, fontWeight: 500, transition: "all 0.15s" }}>
                <span>{b.icon}</span> {b.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Marquee ───────────────────────────────────────────────────────────── */}
      <div style={{ background: LIGHT, borderBottom: "1px solid #dbeafe", overflow: "hidden", padding: "12px 0" }}>
        <div style={{ display: "flex", animation: "marquee 28s linear infinite", width: "max-content" }}>
          {[...Array(2)].flatMap(() =>
            ["AI Video Screening","AI CV Scoring","Performance Reviews","Leave Management","Attendance Tracking","Payroll & Payslips","TH/IN Compliance","Invoice Generation","AI HR Chatbot","Offer Letters","11 HR Letter Types","Public Careers Page","Expense Claims","Asset Tracking","Policy Search (AI)","LinkedIn Job Posts"].map((item, i) => (
              <div key={`${item}-${i}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 24px", whiteSpace: "nowrap" }}>
                <span style={{ color: SKY, fontSize: 8 }}>✦</span>
                <span style={{ color: "#64748b", fontSize: 13, fontWeight: 500 }}>{item}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Features ──────────────────────────────────────────────────────────── */}
      <section id="features" className="section-pad" style={{ padding: "96px 24px", background: "white" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          {/* Heading */}
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: LIGHT, border: `1px solid ${LIGHT2}`, borderRadius: 100, padding: "6px 16px", marginBottom: 16 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: NAVY, letterSpacing: 1 }}>FEATURES</span>
            </div>
            <h2 style={{ fontSize: 40, fontWeight: 900, margin: "0 0 14px", letterSpacing: -1.5, color: "#0f172a" }}>
              Everything HR needs,{" "}
              <span style={{ background: `linear-gradient(135deg,${NAVY},${SKY})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                powered by AI
              </span>
            </h2>
            <p style={{ color: "#64748b", fontSize: 16, maxWidth: 500, margin: "0 auto" }}>
              Built for staffing agencies and growing companies who want to move fast without a big ops team.
            </p>
          </div>

          {/* Feature grid — mobile: cards, desktop: accordion + preview */}
          <div className="features-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 40, alignItems: "start" }}>

            {/* Left: accordion */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {FEATURES.map(f => {
                const isActive = activeFeature === f.id;
                return (
                  <div
                    key={f.id}
                    className="feature-tab"
                    onClick={() => setActiveFeature(f.id)}
                    style={{
                      padding: "16px 18px", borderRadius: 12, cursor: "pointer", transition: "all 0.18s",
                      background: isActive ? LIGHT : "white",
                      border: `1.5px solid ${isActive ? LIGHT2 : "#f1f5f9"}`,
                      borderLeft: `3.5px solid ${isActive ? f.color : "transparent"}`,
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 9, background: isActive ? `linear-gradient(135deg,${f.color},${SKY})` : "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0, transition: "all 0.2s" }}>{f.icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: isActive ? 6 : 0 }}>
                          <span style={{ fontSize: 14, fontWeight: 700, color: isActive ? NAVY : "#334155" }}>{f.title}</span>
                          <span style={{ fontSize: 9, fontWeight: 700, color: f.color, background: `${f.color}15`, padding: "2px 7px", borderRadius: 4, letterSpacing: 0.8 }}>{f.tag}</span>
                        </div>
                        {isActive && (
                          <>
                            <p style={{ color: "#64748b", fontSize: 13, margin: "0 0 10px", lineHeight: 1.7 }}>{f.desc}</p>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                              {f.bullets.map(b => (
                                <div key={b} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569" }}>
                                  <span style={{ width: 16, height: 16, borderRadius: "50%", background: `${f.color}15`, color: f.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, flexShrink: 0 }}>✓</span>
                                  {b}
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      <span style={{ color: isActive ? f.color : "#cbd5e1", fontSize: 18, transition: "transform 0.2s", transform: isActive ? "rotate(90deg)" : "none", flexShrink: 0 }}>›</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right: live mockup */}
            <div className="feature-mockup-col" style={{ position: "sticky", top: 96 }}>
              <div style={{
                background: "white", borderRadius: 18, border: `1.5px solid ${LIGHT2}`,
                padding: 28, boxShadow: "0 8px 32px rgba(30,58,138,0.08)",
                animation: "fade-in 0.2s ease",
                transition: "border-color 0.3s",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, paddingBottom: 14, borderBottom: "1px solid #f1f5f9" }}>
                  <div style={{ width: 32, height: 32, borderRadius: 9, background: `linear-gradient(135deg,${activeF.color},${SKY})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15 }}>{activeF.icon}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{activeF.title}</div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: activeF.color, letterSpacing: 0.6 }}>{activeF.tag}</div>
                  </div>
                </div>
                <FeatureMockup id={activeF.id} color={activeF.color} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ─────────────────────────────────────────────────────────────── */}
      <section style={{ background: `linear-gradient(135deg,${NAVY} 0%,${BLUE} 100%)`, padding: "80px 24px" }}>
        <div className="stats-grid" style={{ maxWidth: 960, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 24 }}>
          {STATS.map(s => (
            <div key={s.label} style={{ textAlign: "center", padding: "24px 16px" }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>{s.icon}</div>
              <div className="stat-val" style={{ fontSize: 44, fontWeight: 900, color: "white", letterSpacing: -2, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", marginTop: 8, fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Value consolidation strip ─────────────────────────────────────────── */}
      <section style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0", padding: "40px 24px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <p style={{ textAlign: "center", fontSize: 12, fontWeight: 700, color: "#94a3b8", letterSpacing: 1, textTransform: "uppercase", marginBottom: 24 }}>
            Replace 3 tools with 1 — Core HRIS included, modules added as you need them
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }} className="compare-grid">
            {[
              { category: "AI Video Screening tools", feature: "AI Video Screening", note: "Typically $300–500/mo standalone", color: "#dc2626" },
              { category: "AI Recruitment platforms", feature: "AI CV Scoring & Pipeline", note: "Typically $100–200/mo standalone", color: "#d97706" },
              { category: "Payroll compliance software", feature: "Thai & Indian Payroll Compliance", note: "Typically $200+/mo standalone", color: "#7c3aed" },
            ].map(c => (
              <div key={c.category} style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{c.category}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{c.feature}</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>{c.note}</span>
                  <span style={{ background: "#dcfce7", color: "#166534", fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20 }}>Included ✓</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ──────────────────────────────────────────────────────── */}
      <section id="how-it-works" className="section-pad" style={{ padding: "96px 24px", background: "#f8fafc" }}>
        <div style={{ maxWidth: 960, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: LIGHT, border: `1px solid ${LIGHT2}`, borderRadius: 100, padding: "6px 16px", marginBottom: 16 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: NAVY, letterSpacing: 1 }}>HOW IT WORKS</span>
            </div>
            <h2 style={{ fontSize: 40, fontWeight: 900, margin: "0 0 12px", letterSpacing: -1.5, color: "#0f172a" }}>
              Up and running in minutes
            </h2>
            <p style={{ color: "#64748b", fontSize: 16 }}>Subscribe, get your setup link by email, and start immediately.</p>
          </div>

          <div className="steps-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 48, position: "relative" }}>
            {/* Connecting line */}
            <div className="steps-line" style={{ position: "absolute", top: 32, left: "20%", right: "20%", height: 2, background: `linear-gradient(90deg,${LIGHT2},${BLUE}40,${LIGHT2})` }} />

            {[
              { num: "01", icon: "🚀", title: "Pick a plan & subscribe", desc: "Choose Starter, Growth, or Pro. Enter your company details and pay securely via Stripe — your workspace is activated instantly." },
              { num: "02", icon: "👥", title: "Invite your team", desc: "Add employees, recruiters, and managers with role-based access. Each person sees exactly what they need." },
              { num: "03", icon: "✅", title: "Go live instantly", desc: "Connect Gmail for CV intake, configure AI settings, and start managing HR — all from one dashboard." },
            ].map(s => (
              <div key={s.num} style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
                <div style={{
                  width: 64, height: 64, borderRadius: "50%",
                  background: `linear-gradient(135deg,${NAVY},${BLUE})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 24, margin: "0 auto 20px",
                  boxShadow: "0 8px 24px rgba(30,58,138,0.2)",
                  position: "relative",
                }}>
                  <div style={{ position: "absolute", inset: -4, borderRadius: "50%", border: `2px solid ${LIGHT2}`, pointerEvents: "none" }} />
                  {s.icon}
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, color: SKY, letterSpacing: 1.5, marginBottom: 8 }}>STEP {s.num}</div>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0f172a", margin: "0 0 10px" }}>{s.title}</h3>
                <p style={{ color: "#64748b", fontSize: 14, lineHeight: 1.75, margin: 0 }}>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────────────────────── */}
      <section className="section-pad" style={{ padding: "96px 24px", background: "white", textAlign: "center" }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          <div style={{ width: 64, height: 64, borderRadius: 16, background: `linear-gradient(135deg,${NAVY},${BLUE})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "0 auto 28px", boxShadow: "0 8px 24px rgba(30,58,138,0.25)" }}>🚀</div>
          <h2 style={{ fontSize: 44, fontWeight: 900, margin: "0 0 16px", letterSpacing: -1.5, color: "#0f172a" }}>
            Ready to transform<br />your HR?
          </h2>
          <p style={{ color: "#64748b", fontSize: 17, margin: "0 0 40px", lineHeight: 1.75 }}>
            Subscribe, invite your team, and be live in under 5 minutes.<br />Core from $79/mo. Cancel anytime.
          </p>

          {/* Email form */}
          <form onSubmit={handleSubmit} style={{ maxWidth: 460, margin: "0 auto 16px" }}>
            <div className="hero-form" style={{ display: "flex", gap: 0, background: LIGHT, border: `1.5px solid ${LIGHT2}`, borderRadius: 12, padding: 6 }}>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter your work email"
                style={{ flex: 1, background: "transparent", border: "none", outline: "none", padding: "11px 14px", color: "#0f172a", fontSize: 14 }}
              />
              <button type="submit" className="cta-primary" style={{ borderRadius: 8, padding: "10px 20px", fontSize: 14, boxShadow: "none", whiteSpace: "nowrap" }}>
                Get Started →
              </button>
            </div>
          </form>

          <p style={{ color: "#94a3b8", fontSize: 12 }}>
            Already have an account?{" "}
            <a href="/login" style={{ color: BLUE, textDecoration: "none", fontWeight: 600 }}>Sign in</a>
          </p>

          {/* Social proof row */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 16, marginTop: 36, paddingTop: 32, borderTop: "1px solid #f1f5f9" }}>
            {TRUST.slice(0, 4).map(b => (
              <div key={b.label} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#64748b" }}>
                <span>{b.icon}</span> {b.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────────────── */}
      <footer style={{ background: "#0f172a", padding: "48px 24px" }}>
        <div className="footer-grid" style={{ maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo-dark.png" alt="HRxAI" style={{ height: 32, width: "auto", objectFit: "contain", mixBlendMode: "screen" }} />
            <span style={{ fontWeight: 800, fontSize: 16, color: "white" }}>HRx<span style={{ color: SKY }}>AI</span></span>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[["Features","#features"], ["How it works","#how-it-works"], ["Sign Up","/signup"], ["Sign In","/login"]].map(([l, h]) => (
              <a key={l} href={h} style={{ color: "rgba(255,255,255,0.4)", textDecoration: "none", fontSize: 13, padding: "6px 12px", borderRadius: 6, transition: "color 0.15s" }}
                onMouseEnter={e => (e.currentTarget.style.color = "white")}
                onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.4)")}
              >{l}</a>
            ))}
          </div>

          <p style={{ color: "rgba(255,255,255,0.2)", fontSize: 12, margin: 0 }}>
            © {new Date().getFullYear()} HRxAI · Sequence Technologies
          </p>
        </div>
      </footer>
    </div>
  );
}
